import moment from 'moment'

export const WEEKLY_SUMMARY_MODES = {
  salesActual: 'total',
  salesPlan: 'total',
  planPercent: 'average',
  actualPercent: 'total',
  actualInvBOM: 'average',
  actualSS: 'average',
  trendedTurn: 'average',
  planSS: 'average',
  planInvBOM: 'average',
  planInflowRetail: 'total',
  planInflowCost: 'total',
  onOrderRetailMtd: 'total',
  onOrderCostMtd: 'total',
  actualInflowRetail: 'none',
  actualInflowCost: 'none',
  markdownActual: 'total',
  markdownPlan: 'total',
  markdownPlanPercent: 'average',
  markdownActualPercent: 'total',
}

export const WEEKLY_MONTH_SUBTOTAL_MODES = {
  salesActual: 'total',
  salesPlan: 'total',
  planPercent: 'total',
  actualPercent: 'total',
  actualInvBOM: 'average',
  actualSS: 'calculated',
  trendedTurn: 'none',
  planSS: 'calculated',
  planInvBOM: 'average',
  planInflowRetail: 'total',
  planInflowCost: 'total',
  onOrderRetailMtd: 'total',
  onOrderCostMtd: 'total',
  actualInflowRetail: 'total',
  actualInflowCost: 'total',
  markdownActual: 'total',
  markdownPlan: 'total',
  markdownPlanPercent: 'total',
  markdownActualPercent: 'total',
}

const WEEKLY_MONTH_SUBTOTAL_SUM_FIELDS = [
  'salesActual',
  'salesPlan',
  'planPercent',
  'actualPercent',
  'planInflowRetail',
  'planInflowCost',
  'onOrderRetailMtd',
  'onOrderCostMtd',
  'actualInflowRetail',
  'actualInflowCost',
  'markdownActual',
  'markdownPlan',
  'markdownPlanPercent',
  'markdownActualPercent',
]

const WEEKLY_MONTH_SUBTOTAL_AVERAGE_FIELDS = ['actualInvBOM', 'planInvBOM']

const sum = (rows, key) => rows.reduce((acc, row) => acc + (Number(row?.[key]) || 0), 0)

const average = (rows, key) => {
  if (!Array.isArray(rows) || !rows.length) return null
  return sum(rows, key) / rows.length
}

const averageDefined = (rows, key) => {
  const list = (Array.isArray(rows) ? rows : [])
    .map((row) => row?.[key])
    .filter((value) => value !== null && typeof value !== 'undefined' && value !== '')
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
  if (!list.length) return null
  return list.reduce((acc, value) => acc + value, 0) / list.length
}

export const toTrendedTurn = (averageStockToSalesRatio, annualizationFactor = 52) => {
  const ratio = Number(averageStockToSalesRatio)
  if (!Number.isFinite(ratio) || ratio <= 0) return null
  const factor = Number(annualizationFactor)
  if (!Number.isFinite(factor) || factor <= 0) return null
  return factor / ratio
}

export const deriveTrendedTurnFromPeriodData = (periodData, { includeRearHistorical = false } = {}) => {
  const rows = (Array.isArray(periodData) ? periodData : []).filter((row) => {
    if (includeRearHistorical) return true
    return !row?.isRearHistoricalPeriod
  })

  const avgWeeklyStockToSales = averageDefined(rows, 'actualSS')
  if (Number.isFinite(avgWeeklyStockToSales) && avgWeeklyStockToSales > 0) {
    return toTrendedTurn(avgWeeklyStockToSales)
  }

  const avgFallbackStockToSales = averageDefined(rows, 'rawTrendedStockToSales')
  return toTrendedTurn(avgFallbackStockToSales)
}

const toFiniteNumber = (value, fallback = 0) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

const resolveContributionShare = (segment, segmentCount) => {
  const days = Number(segment?.days ?? 0)
  if (days > 0) return days
  return segmentCount > 0 ? (1 / segmentCount) : 1
}

const getRowMonthContributions = (row) => {
  const segments = Array.isArray(row?.monthSegments) && row.monthSegments.length
    ? row.monthSegments
    : [{
        monthKey: row?.representativeMonthKey || row?.period || 'month',
        monthLabel: row?.representativeMonthLabel || 'Month',
        days: 7,
      }]

  const rawTotal = segments.reduce((acc, segment) => acc + resolveContributionShare(segment, segments.length), 0)
  const total = rawTotal > 0 ? rawTotal : segments.length

  return segments.map((segment) => ({
    monthKey: String(segment?.monthKey ?? row?.representativeMonthKey ?? row?.period ?? 'month'),
    monthLabel: segment?.monthLabel || row?.representativeMonthLabel || 'Month',
    share: total > 0 ? (resolveContributionShare(segment, segments.length) / total) : 1,
    segment,
  }))
}

const resolveContributionSumValue = (field, row, contribution) => {
  const { share, segment } = contribution
  if (field === 'salesPlan') {
    const segmentValue = Number(segment?.salesPlan)
    if (Number.isFinite(segmentValue)) return segmentValue
  }
  if (field === 'markdownPlan') {
    const segmentValue = Number(segment?.markdownPlan)
    if (Number.isFinite(segmentValue)) return segmentValue
  }
  if (field === 'onOrderRetailMtd') {
    const segmentValue = Number(segment?.onOrderRetailMtd)
    if (Number.isFinite(segmentValue)) return segmentValue
  }
  if (field === 'onOrderCostMtd') {
    const segmentValue = Number(segment?.onOrderCostMtd)
    if (Number.isFinite(segmentValue)) return segmentValue
  }
  return toFiniteNumber(row?.[field]) * share
}

const resolveContributionAverageValue = (field, row, contribution) => {
  const { segment } = contribution
  if (field === 'actualInvBOM') {
    const segmentValue = Number(segment?.actualInvBOM)
    if (Number.isFinite(segmentValue)) return segmentValue
  }
  if (field === 'planInvBOM') {
    const segmentValue = Number(segment?.planInvBOM)
    if (Number.isFinite(segmentValue)) return segmentValue
  }
  return toFiniteNumber(row?.[field])
}

export const getWeeklySummaryLabel = (mode) => {
  if (mode === 'total') return 'Total'
  if (mode === 'average') return 'Avg'
  return null
}

export const formatWeekStartLabel = (dateString, fallbackWeek = null) => {
  if (!dateString) return fallbackWeek ? `EW${fallbackWeek}` : '-'
  const parsed = moment(dateString)
  if (!parsed.isValid()) return fallbackWeek ? `EW${fallbackWeek}` : '-'
  return parsed.format('MMM D')
}

export const formatRepresentativeMonthLabel = (epochMonth, fallbackDate = null) => {
  const em = Number(epochMonth)
  if (Number.isFinite(em) && em >= 190001 && em <= 299912) {
    const parsed = moment(String(em), 'YYYYMM', true)
    if (parsed.isValid()) return parsed.format('MMMM')
  }

  if (Number.isFinite(em)) {
    const parsed = moment('1970-01-01').add(em, 'months')
    if (parsed.isValid()) return parsed.format('MMMM')
  }

  if (fallbackDate) {
    const parsed = moment(fallbackDate)
    if (parsed.isValid()) return parsed.format('MMMM')
  }

  return 'Unknown Month'
}

export const parsePlannerEpochMonth = (epochMonth, fallbackDate = null) => {
  const em = Number(epochMonth)
  if (Number.isFinite(em) && em >= 190001 && em <= 299912) {
    const parsed = moment(String(em), 'YYYYMM', true)
    if (parsed.isValid()) return parsed
  }

  if (Number.isFinite(em)) {
    const parsed = moment('1970-01-01').add(em, 'months')
    if (parsed.isValid()) return parsed
  }

  if (fallbackDate) {
    const parsed = moment(fallbackDate)
    if (parsed.isValid()) return parsed
  }

  return null
}

const countMonthWeekday = (monthMoment, weekday) => {
  if (!monthMoment?.isValid?.()) return null
  const cursor = monthMoment.clone().startOf('month')
  const end = monthMoment.clone().endOf('month')
  let count = 0

  while (cursor.isSameOrBefore(end, 'day')) {
    if (cursor.day() === weekday) count += 1
    cursor.add(1, 'day')
  }

  return count
}

export const getMonthSaturdayComparisonLabel = (epochMonth, fallbackDate = null) => {
  const monthMoment = parsePlannerEpochMonth(epochMonth, fallbackDate)
  if (!monthMoment?.isValid?.()) return null

  const currentYearCount = countMonthWeekday(monthMoment, 6)
  const previousYearCount = countMonthWeekday(monthMoment.clone().subtract(1, 'year'), 6)
  if (!Number.isFinite(currentYearCount) || !Number.isFinite(previousYearCount)) return null

  return `${currentYearCount} / ${previousYearCount}`
}

export const formatMonthLabelWithSaturdayComparison = (epochMonth, fallbackDate = null, baseLabel = null) => {
  const resolvedBaseLabel = typeof baseLabel === 'string' && baseLabel.trim().length
    ? baseLabel.trim()
    : formatRepresentativeMonthLabel(epochMonth, fallbackDate)
  const saturdayComparison = getMonthSaturdayComparisonLabel(epochMonth, fallbackDate)

  if (!saturdayComparison) return resolvedBaseLabel
  if (resolvedBaseLabel.endsWith(saturdayComparison)) return resolvedBaseLabel
  return `${resolvedBaseLabel} ${saturdayComparison}`
}

export const buildWeeklySummary = (periodData) => {
  const rows = Array.isArray(periodData) ? periodData : []

  return {
    values: {
      salesActual: sum(rows, 'salesActual'),
      salesPlan: sum(rows, 'salesPlan'),
      planPercent: average(rows, 'planPercent'),
      actualPercent: sum(rows, 'actualPercent'),
      actualInvBOM: average(rows, 'actualInvBOM'),
      actualSS: average(rows, 'actualSS'),
      trendedTurn: deriveTrendedTurnFromPeriodData(rows),
      planSS: average(rows, 'planSS'),
      planInvBOM: average(rows, 'planInvBOM'),
      planInflowRetail: sum(rows, 'planInflowRetail'),
      planInflowCost: sum(rows, 'planInflowCost'),
      onOrderRetailMtd: sum(rows, 'onOrderRetailMtd'),
      onOrderCostMtd: sum(rows, 'onOrderCostMtd'),
      actualInflowRetail: null,
      actualInflowCost: null,
      markdownActual: sum(rows, 'markdownActual'),
      markdownPlan: sum(rows, 'markdownPlan'),
      markdownPlanPercent: average(rows, 'markdownPlanPercent'),
      markdownActualPercent: sum(rows, 'markdownActualPercent'),
    },
    modes: { ...WEEKLY_SUMMARY_MODES },
  }
}

export const buildWeeklyMonthSubtotals = (periodData) => {
  const rows = Array.isArray(periodData) ? periodData : []
  if (!rows.length) return []

  const monthAccumulatorByKey = new Map()

  rows.forEach((row, rowIndex) => {
    getRowMonthContributions(row).forEach((contribution) => {
      const monthKey = String(contribution.monthKey)
      if (!monthAccumulatorByKey.has(monthKey)) {
        monthAccumulatorByKey.set(monthKey, {
          monthKey,
          monthLabel: contribution.monthLabel,
          insertAfterPeriod: String(row?.period ?? rowIndex),
          order: rowIndex,
          sums: Object.fromEntries(WEEKLY_MONTH_SUBTOTAL_SUM_FIELDS.map((field) => [field, 0])),
          averages: Object.fromEntries(WEEKLY_MONTH_SUBTOTAL_AVERAGE_FIELDS.map((field) => [field, { total: 0, count: 0 }])),
          monthlyReferenceSalesActual: 0,
          monthlyReferenceSalesPlan: 0,
          monthlyReferenceMarkdownActual: 0,
          monthlyReferenceMarkdownPlan: 0,
        })
      }

      const accumulator = monthAccumulatorByKey.get(monthKey)
      accumulator.monthLabel = contribution.monthLabel || accumulator.monthLabel
      accumulator.insertAfterPeriod = String(row?.period ?? rowIndex)
      accumulator.order = rowIndex

      // Capture monthly reference values only for the representative month of the row.
      // Split-week rows can contribute visible sums into adjacent months, but the full
      // monthly reference belongs to the row's anchor month only.
      const rowSalesRef = toFiniteNumber(row?.salesPlanMonthlyReference)
      const rowSalesActualRef = toFiniteNumber(row?.salesActualMonthlyReference)
      const rowMdRef = toFiniteNumber(row?.markdownPlanMonthlyReference)
      const rowMdActualRef = toFiniteNumber(row?.markdownActualMonthlyReference)
      const rowReferenceMonthKey = String(row?.representativeMonthKey ?? contribution.monthKey)
      if (rowReferenceMonthKey === monthKey) {
        if (rowSalesActualRef > 0) accumulator.monthlyReferenceSalesActual = rowSalesActualRef
        if (rowSalesRef > 0) accumulator.monthlyReferenceSalesPlan = rowSalesRef
        if (rowMdActualRef > 0) accumulator.monthlyReferenceMarkdownActual = rowMdActualRef
        if (rowMdRef > 0) accumulator.monthlyReferenceMarkdownPlan = rowMdRef
      }

      WEEKLY_MONTH_SUBTOTAL_SUM_FIELDS.forEach((field) => {
        accumulator.sums[field] += resolveContributionSumValue(field, row, contribution)
      })

      WEEKLY_MONTH_SUBTOTAL_AVERAGE_FIELDS.forEach((field) => {
        const value = resolveContributionAverageValue(field, row, contribution)
        if (!Number.isFinite(value)) return
        accumulator.averages[field].total += value
        accumulator.averages[field].count += 1
      })
    })
  })

  return Array.from(monthAccumulatorByKey.values())
    .sort((a, b) => a.order - b.order)
    .map((accumulator) => {
      const actualInvBOM = accumulator.averages.actualInvBOM.count
        ? accumulator.averages.actualInvBOM.total / accumulator.averages.actualInvBOM.count
        : 0
      const planInvBOM = accumulator.averages.planInvBOM.count
        ? accumulator.averages.planInvBOM.total / accumulator.averages.planInvBOM.count
        : 0
      const visibleSalesActualSum = accumulator.sums.salesActual
      const salesActual = accumulator.monthlyReferenceSalesActual > 0
        ? accumulator.monthlyReferenceSalesActual
        : visibleSalesActualSum
      const visibleMarkdownActualSum = accumulator.sums.markdownActual
      const markdownActual = accumulator.monthlyReferenceMarkdownActual > 0
        ? accumulator.monthlyReferenceMarkdownActual
        : visibleMarkdownActualSum
      const visibleSalesPlanSum = accumulator.sums.salesPlan
      const salesPlan = (accumulator.monthlyReferenceSalesPlan > 0 && accumulator.monthlyReferenceSalesPlan > visibleSalesPlanSum + 0.01)
        ? accumulator.monthlyReferenceSalesPlan
        : visibleSalesPlanSum
      const visibleMarkdownPlanSum = accumulator.sums.markdownPlan
      const markdownPlan = (accumulator.monthlyReferenceMarkdownPlan > 0 && accumulator.monthlyReferenceMarkdownPlan > visibleMarkdownPlanSum + 0.01)
        ? accumulator.monthlyReferenceMarkdownPlan
        : visibleMarkdownPlanSum

      return {
        monthKey: accumulator.monthKey,
        monthLabel: accumulator.monthLabel,
        insertAfterPeriod: accumulator.insertAfterPeriod,
        values: {
          salesActual,
          salesPlan,
          planPercent: salesPlan > 0 ? 100 : 0,
          actualPercent: salesActual > 0 ? 100 : 0,
          actualInvBOM,
          actualSS: salesActual ? (actualInvBOM / salesActual) : 0,
          trendedTurn: null,
          planSS: salesPlan ? (planInvBOM / salesPlan) : 0,
          planInvBOM,
          planInflowRetail: accumulator.sums.planInflowRetail,
          planInflowCost: accumulator.sums.planInflowCost,
          onOrderRetailMtd: accumulator.sums.onOrderRetailMtd,
          onOrderCostMtd: accumulator.sums.onOrderCostMtd,
          actualInflowRetail: accumulator.sums.actualInflowRetail,
          actualInflowCost: accumulator.sums.actualInflowCost,
          markdownActual,
          markdownPlan,
          markdownPlanPercent: markdownPlan > 0 ? 100 : 0,
          markdownActualPercent: markdownActual > 0 ? 100 : 0,
        },
        modes: { ...WEEKLY_MONTH_SUBTOTAL_MODES },
      }
    })
}
